import { useLocation, useNavigate } from "react-router-dom";
import { getBrowserHash, splitHash, updateHash, type HashParamKey } from "../../utils/urlHash";
import { sessionStatusIds, type SessionFilterValues } from "./sessionFilterModel";

export const TEAM_SEARCH_HASH_KEY = "peopleSearch";
export const TEAM_SORT_HASH_KEY = "peopleSort";
export const SESSION_SEARCH_HASH_KEY = "rs";
export const SESSION_ARCHIVE_HASH_KEY = "ra";
export const SESSION_STATUS_HASH_KEY = "rstatus";
export const SESSION_DURATION_MIN_HASH_KEY = "rdMin";
export const SESSION_DURATION_MAX_HASH_KEY = "rdMax";

const legacySessionKeys = {
  rs: "search",
  ra: "sessionsArchive",
  rstatus: "sessionsStatus",
  rdMin: "sessionsMinDuration",
  rdMax: "sessionsMaxDuration",
} as const;

function useFilterUrl() {
  const location = useLocation();
  const navigate = useNavigate();
  return {
    params: splitHash(location.hash).params,
    search: new URLSearchParams(location.search),
    replace: (updates: Partial<Record<HashParamKey, string | null>>, search = location.search) =>
      navigate(
        {
          pathname: location.pathname,
          search,
          // People and dates can update the live hash without notifying the router.
          hash: updateHash(getBrowserHash(), updates),
        },
        { replace: true, state: location.state },
      ),
  };
}

/** The same session criteria apply in My Activity and the selected team member's list. */
export function useSessionFilters() {
  const url = useFilterUrl();
  // Old query-string links still work; the next edit moves them to the canonical hash.
  const read = (key: keyof typeof legacySessionKeys) =>
    url.params.get(key) ?? url.search.get(legacySessionKeys[key]);
  const duration = (key: "rdMin" | "rdMax") => {
    const raw = read(key);
    return raw !== null && raw.trim() !== "" && Number.isFinite(Number(raw)) && Number(raw) >= 0
      ? Number(raw)
      : null;
  };
  const archive = read("ra");
  const status = read("rstatus");
  const filters: SessionFilterValues = {
    query: read("rs") ?? "",
    archive: archive === "archived" || archive === "all" ? archive : "active",
    status:
      status === null || status === "all"
        ? sessionStatusIds
        : sessionStatusIds.filter(value => status.split(",").includes(value)),
    min: duration("rdMin"),
    max: duration("rdMax"),
  };
  const onChange = (patch: Partial<SessionFilterValues>) => {
    const next = { ...filters, ...patch };
    for (const key of Object.values(legacySessionKeys)) url.search.delete(key);
    url.replace(
      {
        rs: next.query || null,
        ra: next.archive === "active" ? null : next.archive,
        [SESSION_STATUS_HASH_KEY]:
          next.status.length === sessionStatusIds.length ? null : next.status.join(",") || "none",
        rdMin: next.min === null ? null : String(next.min),
        rdMax: next.max === null ? null : String(next.max),
      },
      url.search.toString(),
    );
  };
  return { filters, onChange };
}

const TEAM_SORTS = ["time", "process_time", "processes", "sessions", "recent", "name"];

export function useTeamActivityFilters() {
  const url = useFilterUrl();
  const sort = url.params.get(TEAM_SORT_HASH_KEY) ?? "time";
  const filters = {
    query: url.params.get(TEAM_SEARCH_HASH_KEY) ?? "",
    sort: TEAM_SORTS.includes(sort) ? sort : "time",
  };
  return {
    ...filters,
    onChange: (patch: Partial<typeof filters>) => {
      const next = { ...filters, ...patch };
      url.replace({
        [TEAM_SEARCH_HASH_KEY]: next.query || null,
        [TEAM_SORT_HASH_KEY]: next.sort === "time" ? null : next.sort,
      });
    },
  };
}
